import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Crown, 
  Heart, 
  Shield, 
  Brain, 
  Lightbulb, 
  Users,
  Sparkles,
  CheckCircle2,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Calendar,
  BookOpen,
  MessageCircle,
  Flame,
  Zap
} from 'lucide-react';

const freeTierFeatures = [
  { text: "Daily Crown Declaration", included: true, icon: Calendar },
  { text: "1 Educational intro lesson per module", included: true, icon: BookOpen },
  { text: "Basic no-contact day counter", included: true, icon: Flame },
  { text: "View community prayer wall", included: true, icon: Users },
  { text: "AI 'Adjust My Crown' support tool", included: false, icon: Crown },
  { text: "Full healing modules (20 lessons)", included: false, icon: BookOpen },
  { text: "Urge logging & milestone tracking", included: false, icon: Heart },
  { text: "Post prayer requests", included: false, icon: MessageCircle },
  { text: "Boundary script templates", included: false, icon: Shield },
  { text: "Nervous system exercises", included: false, icon: Brain },
];

const premiumFeatures = [
  { text: "Daily Crown Declaration", included: true, icon: Calendar },
  { text: "1 Educational intro lesson per module", included: true, icon: BookOpen },
  { text: "Basic no-contact day counter", included: true, icon: Flame },
  { text: "View community prayer wall", included: true, icon: Users },
  { text: "AI 'Adjust My Crown' support tool", included: true, icon: Crown, highlight: true },
  { text: "Full healing modules (20 lessons)", included: true, icon: BookOpen, highlight: true },
  { text: "Urge logging & milestone tracking", included: true, icon: Heart, highlight: true },
  { text: "Post prayer requests", included: true, icon: MessageCircle, highlight: true },
  { text: "Boundary script templates", included: true, icon: Shield, highlight: true },
  { text: "Nervous system exercises", included: true, icon: Brain, highlight: true },
];

const benefits = [
  {
    icon: Crown,
    title: "AI-Powered Support",
    description: "When you're triggered, doubting yourself, or tempted to reach out—get instant, Christ-centered truth and guidance.",
    color: "from-amber-400 to-amber-600",
    premium: true
  },
  {
    icon: Heart,
    title: "Break Trauma Bonds",
    description: "Track your no-contact streak, resist urges, and celebrate milestones as you break free from unhealthy attachments.",
    color: "from-rose-400 to-rose-600",
    premium: true
  },
  {
    icon: Lightbulb,
    title: "Reclaim Your Reality",
    description: "Recover from gaslighting with clarity worksheets and cognitive reframing exercises that rebuild trust in yourself.",
    color: "from-purple-400 to-purple-600",
    premium: true
  },
  {
    icon: Shield,
    title: "Build Strong Boundaries",
    description: "Learn to set and maintain healthy boundaries with script templates, rehearsal prompts, and guilt-dismantling exercises.",
    color: "from-emerald-400 to-emerald-600",
    premium: true
  },
  {
    icon: Brain,
    title: "Regulate Your Nervous System",
    description: "Calm anxiety and panic with 60-second grounding exercises, breathwork, and panic-to-peace protocols.",
    color: "from-blue-400 to-blue-600",
    premium: true
  },
  {
    icon: Users,
    title: "Sisterhood Community",
    description: "You're not alone. Connect with women on the same healing journey through our supportive prayer wall.",
    color: "from-pink-400 to-pink-600",
    premium: false
  }
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center">
            <Crown className="w-6 h-6 text-purple-950" />
          </div>
          <span className="font-serif text-xl font-semibold">Reclaiming My Crown</span>
        </button>
        <Button 
          onClick={() => navigate('/')}
          variant="outline"
          className="rounded-full px-6 border-primary text-primary hover:bg-primary/5"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            About Reclaiming My Crown
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground leading-tight mb-6">
            Recovery with <span className="text-primary">Refinement</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A structured, Christ-centered recovery space for women healing from narcissistic abuse. 
            Not dramatic. Not reactive. Not bitter. <strong>Grounded. Educated. Restorative. Kingdom-centered.</strong>
          </p>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 px-6 bg-purple-50/50 dark:bg-purple-950/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
              What Makes Us Different
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Many "narcissistic abuse" spaces focus on venting, exposing, and endless diagnosis. 
              We focus on what actually heals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="crown-card p-6">
              <h3 className="font-semibold text-destructive mb-4">Others Focus On:</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-destructive" /> Venting without resolution</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-destructive" /> Exposing the narcissist</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-destructive" /> Staying stuck in anger</li>
                <li className="flex items-center gap-2"><XCircle className="w-4 h-4 text-destructive" /> Endless diagnosis</li>
              </ul>
            </div>
            <div className="crown-card p-6 border-primary/30">
              <h3 className="font-semibold text-primary mb-4">We Focus On:</h3>
              <ul className="space-y-2 text-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Identity restoration</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Nervous system regulation</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Trauma bond detachment</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Boundary formation & peace</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
              How We Help You Heal
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="crown-card p-6 relative">
                  {benefit.premium && (
                    <span className="absolute top-4 right-4 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium">
                      Premium
                    </span>
                  )}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-6 bg-purple-50/50 dark:bg-purple-950/20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
              Choose Your Path
            </h2>
            <p className="text-muted-foreground">
              Start free, upgrade when you're ready for deeper healing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Tier */}
            <div className="crown-card p-8">
              <div className="text-center mb-6">
                <h3 className="font-serif text-2xl font-bold text-foreground mb-2">Free</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground">/forever</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Start your healing journey</p>
              </div>

              <div className="space-y-3 mb-8">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">What's included:</p>
                {freeTierFeatures.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      {feature.included ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-muted-foreground/30 flex-shrink-0" />
                      )}
                      <Icon className={`w-4 h-4 flex-shrink-0 ${feature.included ? 'text-primary' : 'text-muted-foreground/30'}`} />
                      <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50 line-through'}>
                        {feature.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full rounded-full py-5 border-2 border-primary text-primary hover:bg-primary/5"
              >
                Start Free
              </Button>
            </div>

            {/* Premium Tier */}
            <div className="crown-card p-8 border-2 border-amber-400/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 gold-gradient"></div>
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold">
                  BEST VALUE
                </span>
              </div>
              
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full gold-gradient mb-3">
                  <Crown className="w-6 h-6 text-purple-950" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-foreground mb-2">Crown Premium</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">$27</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Full access to all healing tools</p>
              </div>

              <div className="space-y-3 mb-8">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Everything in Free, plus:</p>
                {premiumFeatures.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className={`flex items-center gap-3 text-sm ${feature.highlight ? 'font-medium' : ''}`}>
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${feature.highlight ? 'text-amber-500' : 'text-green-500'}`} />
                      <Icon className={`w-4 h-4 flex-shrink-0 ${feature.highlight ? 'text-amber-600' : 'text-primary'}`} />
                      <span className="text-foreground">
                        {feature.text}
                        {feature.highlight && <Sparkles className="w-3 h-3 inline ml-1 text-amber-500" />}
                      </span>
                    </div>
                  );
                })}
              </div>

              <Button 
                onClick={() => navigate('/')}
                className="w-full btn-gold rounded-full py-5 text-lg"
              >
                <Crown className="w-5 h-5 mr-2" />
                Get Premium
              </Button>
              
              <p className="text-center text-xs text-muted-foreground mt-4">
                Cancel anytime. 7-day money-back guarantee.
              </p>
            </div>
          </div>

          {/* Feature Comparison Note */}
          <div className="mt-10 text-center max-w-2xl mx-auto">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Not sure?</strong> Start with the free tier to explore daily declarations and the community. 
              Upgrade to Premium when you're ready for AI support, full modules, and advanced tracking.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="royal-gradient rounded-3xl p-12 text-white">
            <Crown className="w-16 h-16 mx-auto mb-6 text-amber-300" />
            <h2 className="font-serif text-3xl font-bold mb-4">
              Ready to Reclaim Your Crown?
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Your healing journey starts with one step. Join thousands of women rebuilding their identity, boundaries, and peace.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="btn-gold rounded-full px-8 py-6 text-lg"
            >
              Begin Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-purple-100 dark:border-purple-800/30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            <span className="text-sm text-muted-foreground">© 2026 Reclaiming My Crown</span>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Grounded. Educated. Restorative. Kingdom-centered.
          </p>
        </div>
      </footer>
    </div>
  );
}
